import { gql } from '@apollo/client'
import { isEmpty } from 'ramda'
import { useSort } from '@btime/react-contexts'
import React from 'react'

import { serviceOrdersClient } from '../../../../utils/apollo/api'
import { usePaginatedQuery } from '../../../../utils/apollo/_helpers/pagination'
import PaginationControl from '../../../../components/helpers/PaginationControl'
import StatePresenter from '../../../../packages/kiti/components/helpers/StatePresenter'
import { convertFilterToRelativeDates } from '../../../../utils/helpers/date'

const Context = React.createContext()
const { Provider } = Context
export const useServiceOrdersState = () => React.useContext(Context) || {}
export const useServiceOrders = () => useServiceOrdersState()?.serviceOrders || []

const QUERY = gql`
  query ServiceOrders(
    $page: Int
    $search: String
    $searchType: ServiceOrderSearchType
    $sort: ServiceOrderSort
    $filter: ServiceOrderFilter
  ) {
    serviceOrders(page: $page, search: $search, searchType: $searchType, sort: $sort, filter: $filter) {
      id
      name
      yearlyId
      insertedAt
      endDate
      scheduling
      childrenCount
      slaExpiresAt
      priority {
        id
        name
      }
      tagId
      tag {
        id
        name
        color
      }
      status {
        id
        name
        label
        color
      }
      checklist {
        id
        name
      }
      place {
        id
        name
        resume
      }
      asset {
        id
        name
        type {
          id
          name
        }
      }
      group {
        id
        name
      }
      groups {
        id
        name
      }
      user {
        id
        name
      }
    }
  }
`

const ServiceOrdersLoader = ({
  children,
  search,
  searchType,
  filter,
  defaultSort = { field: 'ID', type: 'DESC' },
  uncontrolledChildren
}) => {
  const [page, setPage] = React.useState(1)
  const { field, type } = useSort()
  const { field: defaultField, type: defaultType } = defaultSort
  filter = convertFilterToRelativeDates(filter)

  if (isEmpty(filter) && !search) {
    filter = { ...filter, with_parent: false }
  }

  const { data, refetch, fetchMore, ...state } = usePaginatedQuery(QUERY, {
    variables: {
      page, //
      search,
      searchType: !!searchType && !!search ? searchType : undefined,
      sort: { field: field || defaultField, type: type || defaultType },
      filter
    },
    flexiblePollInterval: 25000,
    client: serviceOrdersClient
  })

  return (
    <Provider value={data}>
      <StatePresenter {...state} uncontrolledChildren={uncontrolledChildren}>
        {children}
      </StatePresenter>
      <PaginationControl fetchMore={fetchMore} page={page} onChange={setPage} state={state} />
    </Provider>
  )
}

export default ServiceOrdersLoader
